import { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLanguage } from '@/contexts/LanguageContext';
import { useGetAvailableSourceTables } from '@/api/control/control-plane/control-plane';
import {
  AvailableTableDisabledReason,
  type AvailableSourceTableDto,
  type AvailableBusinessCentralSourceTableDto,
  type AvailableSalesforceSourceTableDto,
} from '@/api/control/missionImpactAPI.schemas';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import Fuse from 'fuse.js';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BcApiEndpointBadge } from './BcApiEndpointBadge';
import { SalesforceEntityTypeBadge } from './SalesforceEntityTypeBadge';
import { TableListPastePanel } from './TableListPastePanel';
import { cn } from '@/lib/utils';
import { exceedsIdentifierLimit } from '@/lib/tablePrefix';
import {
  applySelectAll,
  buildTableConfig,
  computeSelectAllState,
  type SelectedTableConfig,
} from '@/lib/sourceTableSelection';

// Re-exported so existing consumers can keep importing the type from the picker.
export type { SelectedTableConfig };

/**
 * UI strings supplied by the parent so the picker doesn't need its own
 * i18n namespace. Both the dialog and the wizard step have these in their
 * existing namespaces and route them through.
 */
interface SourceTablePickerStrings {
  /** Label rendered above the table list (e.g. "Tables"). */
  tablesLabel: string;
  /** Suffix for the selection counter (e.g. "selected" → "3 selected"). */
  selectedSuffix: string;
  /** Placeholder text for the search input. */
  searchPlaceholder: string;
  /** Loading message shown while available tables are being fetched. */
  loadingMessage: string;
  /** Empty-state shown when search yields no results. */
  noTablesFound: string;
  /** Empty-state shown when there are no tables at all. */
  noTablesAvailable: string;
  /** Label above the BC ODataV4 timestamp-field Select. */
  timestampFieldLabel: string;
  /** Placeholder text inside the BC ODataV4 timestamp-field Select. */
  timestampFieldPlaceholder: string;
  /** Label for the "no timestamp field" option in the ODataV4 Select. */
  timestampFieldNone: string;
  /** Hint shown for V2 BC tables when an incremental field is set. Must contain "{field}". */
  incrementalSyncVia: string;
  /** Hint shown for V2 BC tables when no incremental field is available. */
  fullReplication: string;
  /**
   * Notice shown inside an Alert for ODataV4 BC tables that have no datetime
   * columns at all, explaining that every sync will be a full replication.
   */
  noTimestampFieldNotice: string;
  /**
   * Reason shown for a row whose prefixed staging table name would exceed the
   * SQL identifier limit under the target's table prefix, so it cannot be selected.
   */
  nameTooLongForPrefix: string;
  /** Label for the select-all header control when not all filtered tables are selected. */
  selectAllLabel: string;
  /** Label for the select-all header control when all filtered tables are already selected. */
  deselectAllLabel: string;
}

/**
 * Props for the shared source table picker.
 */
export interface SourceTablePickerProps {
  /** Owning organization ID, passed through to the API. */
  orgId: string;
  /** Owning profile ID, passed through to the API. */
  profileId: string;
  /** Source ID whose available tables we are listing. */
  sourceId: string;
  /**
   * The target's optional table prefix. When set, a table whose prefixed staging name
   * (<c>stage_{prefix}{logicalName}</c>) would exceed the 128-char SQL identifier limit
   * is disabled and cannot be selected.
   */
  tablePrefix?: string;
  /**
   * Whether the picker should fetch available tables. Useful for the dialog
   * use-case where we only want to fetch when the dialog is open. Defaults
   * to <c>true</c>.
   */
  enabled?: boolean;
  /** Controlled selection state, keyed by table logical name. */
  value: Map<string, SelectedTableConfig>;
  /** Callback fired with the next selection state on every change. */
  onChange: (next: Map<string, SelectedTableConfig>) => void;
  /** UI strings owned by the parent. */
  strings: SourceTablePickerStrings;
  /**
   * Tailwind class controlling the height of the scroll area. Different
   * callers want different heights (dialog: 400px, wizard step: 500px).
   */
  scrollAreaClassName?: string;
  /**
   * Whether the "select all" header control is available. Server-driven per-org capability
   * (issue #451): when false (the default) the control is hidden entirely; when true it selects
   * or deselects all eligible tables in the current (filtered) view.
   */
  selectAllEnabled?: boolean;
}

/**
 * Shared source table picker used by both the profile creation wizard and
 * the "add source tables" dialog for existing profiles. Owns the data fetch,
 * fuzzy search, list rendering, and the per-row Business Central config UI.
 * The selection state is fully controlled by the parent so the parent can
 * drive submit and validation logic on top of it.
 *
 * Uses @tanstack/react-virtual to render only the rows visible in the
 * viewport, keeping DOM node count constant regardless of list size.
 */
export function SourceTablePicker({
  orgId,
  profileId,
  sourceId,
  tablePrefix,
  enabled = true,
  value,
  onChange,
  strings,
  scrollAreaClassName = 'h-[400px]',
  selectAllEnabled = false,
}: SourceTablePickerProps) {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch available source tables. Gated on `enabled` so the dialog can
  // avoid hitting the API until it is opened.
  const { data: availableTables, isLoading: isLoadingTables } = useGetAvailableSourceTables(
    orgId,
    profileId,
    sourceId,
    {
      query: {
        enabled: enabled && !!sourceId,
      },
    }
  );

  // Narrow the API response to the polymorphic discriminated union so we can
  // access BC-specific fields without ad-hoc casts at every call site.
  const allTables = availableTables?.data;

  // Tables already added to the source are returned (flagged AlreadyAdded) only so a pasted list
  // can report them; the picker's list, search and select-all never show them.
  const availableTablesTyped = useMemo(
    () => allTables?.filter((table) => table.disabledReason !== AvailableTableDisabledReason.AlreadyAdded),
    [allTables],
  );

  // Setup Fuse.js for fuzzy searching.
  const fuse = useMemo(() => {
    if (!availableTablesTyped) return null;
    return new Fuse(availableTablesTyped, {
      keys: ['logicalName', 'displayName'],
      threshold: 0.3,
      includeScore: true,
    });
  }, [availableTablesTyped]);

  // Perform fuzzy search; show all results when no query is set.
  const filteredTables = useMemo<AvailableSourceTableDto[]>(() => {
    if (!availableTablesTyped) return [];
    if (!searchQuery.trim()) return availableTablesTyped;
    if (!fuse) return [];
    return fuse.search(searchQuery).map((r) => r.item);
  }, [availableTablesTyped, searchQuery, fuse]);

  // Virtual scrolling: only the rows visible in the viewport are in the DOM.
  // measureElement lets each row report its own height so expanded BC rows
  // (with timestamp selectors) don't confuse the layout.
  const virtualizer = useVirtualizer({
    count: filteredTables.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 52, // collapsed row height in px
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 5,
    // Replicate the original p-4 top/bottom padding without breaking absolute positioning.
    paddingStart: 16,
    paddingEnd: 16,
  });

  /**
   * Toggles selection of a table. For BC tables, seeds the per-table config
   * with the right defaults: V2 tables auto-resolve their default timestamp
   * field; ODataV4 tables start in full-replication mode until the user
   * picks a field.
   */
  const handleToggleTable = (table: AvailableSourceTableDto) => {
    if (!table.isEnabled) return;
    // Block selecting a table whose prefixed staging name would exceed the SQL identifier limit.
    if (exceedsIdentifierLimit(tablePrefix ?? '', table.logicalName)) return;

    const next = new Map(value);
    if (next.has(table.logicalName)) {
      next.delete(table.logicalName);
    } else {
      next.set(table.logicalName, buildTableConfig(table));
    }
    onChange(next);
  };

  // Select-all header state, computed over the currently filtered tables and skipping
  // ineligible rows (server-disabled or identifier-limit-exceeding). Only used when the
  // per-org capability is enabled.
  const selectAllState = computeSelectAllState(filteredTables, tablePrefix ?? '', value);

  /**
   * Toggles selection of every eligible table in the current (filtered) view. Selects all when
   * not already fully selected; otherwise deselects those filtered tables. Selections outside the
   * active filter are preserved.
   */
  const handleToggleSelectAll = () => {
    onChange(applySelectAll(filteredTables, tablePrefix ?? '', value));
  };

  /**
   * Updates the timestamp field selection for a BC ODataV4 table. The
   * sentinel value "__none__" clears the timestamp field (full replication).
   */
  const handleTimestampFieldChange = (logicalName: string, selected: string) => {
    const existing = value.get(logicalName);
    if (!existing) return;
    const next = new Map(value);
    next.set(logicalName, {
      ...existing,
      timestampField: selected === '__none__' ? undefined : selected,
    });
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label>{strings.tablesLabel}</Label>
        <div className="flex items-center gap-4">
          {selectAllEnabled && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all-tables"
                checked={
                  selectAllState.allSelected
                    ? true
                    : selectAllState.someSelected
                      ? 'indeterminate'
                      : false
                }
                onCheckedChange={handleToggleSelectAll}
                disabled={selectAllState.eligibleCount === 0}
              />
              <Label
                htmlFor="select-all-tables"
                className="text-sm font-normal text-muted-foreground cursor-pointer"
              >
                {selectAllState.allSelected ? strings.deselectAllLabel : strings.selectAllLabel}
              </Label>
            </div>
          )}
          <span className="text-sm text-muted-foreground">
            {value.size} {strings.selectedSuffix}
          </span>
        </div>
      </div>
      {allTables && (
        <TableListPastePanel
          tables={allTables}
          tablePrefix={tablePrefix ?? ''}
          value={value}
          onChange={onChange}
          nameTooLongForPrefix={strings.nameTooLongForPrefix}
        />
      )}
      <Input
        placeholder={strings.searchPlaceholder}
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); }}
        className="mb-2"
      />
      {isLoadingTables ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2 text-sm text-muted-foreground">{strings.loadingMessage}</span>
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className={cn('overflow-y-auto border rounded-md', scrollAreaClassName)}
        >
          {filteredTables.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {searchQuery.trim() ? strings.noTablesFound : strings.noTablesAvailable}
            </div>
          ) : (
            // Outer div establishes the full virtual scroll height so the
            // scrollbar reflects the true list length.
            <div style={{ height: `${String(virtualizer.getTotalSize())}px`, position: 'relative' }}>
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const table = filteredTables[virtualItem.index];
                const isSelected = value.has(table.logicalName);
                const config = value.get(table.logicalName);
                // A table is unselectable if the server disabled it, or if its prefixed
                // staging name would exceed the SQL identifier limit under this target's prefix.
                const tooLong = exceedsIdentifierLimit(tablePrefix ?? '', table.logicalName);
                const rowDisabled = !table.isEnabled || tooLong;
                const bcTable: AvailableBusinessCentralSourceTableDto | null =
                  table.$type === 'BusinessCentral' ? table : null;
                const sfTable: AvailableSalesforceSourceTableDto | null =
                  table.$type === 'Salesforce' ? table : null;
                const dateTimeFields = bcTable?.dateTimeFields;
                const apiEndpoint = bcTable?.apiEndpoint;
                const rowKey = `${table.logicalName}-${apiEndpoint ?? ''}`;
                const nameText = table.displayName || table.logicalName;
                // Show the schema (logical) name alongside the display name when they
                // differ, e.g. "Case (incident)", so a table is identifiable by either.
                const showLogicalName =
                  !!table.displayName && table.displayName !== table.logicalName;

                return (
                  // data-index lets measureElement track row height changes
                  // (e.g. when a BC row expands to show the timestamp selector).
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${String(virtualItem.start)}px)`,
                    }}
                  >
                    {/* px-4 + pb-2 restore the original p-4/space-y-2 spacing.
                        This wrapper is inside the absolute element so it respects
                        the scroll container's content area rather than its padding edge. */}
                    <div className="px-4 pb-2">
                    <div
                      className={cn(
                        'flex flex-col space-y-2 p-3 rounded-md border',
                        rowDisabled && 'opacity-50 bg-muted/50'
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id={rowKey}
                          checked={isSelected}
                          onCheckedChange={() => { handleToggleTable(table); }}
                          disabled={rowDisabled}
                          className="mt-1"
                        />
                        <div className="flex-1 space-y-1">
                          <Label
                            htmlFor={rowKey}
                            className={cn(
                              'flex items-center gap-2 text-sm font-medium cursor-pointer',
                              rowDisabled && 'cursor-not-allowed'
                            )}
                          >
                            <span className="flex items-center gap-1">
                              {rowDisabled && (
                                <AlertTriangle className="h-3 w-3" />
                              )}
                              <span>{nameText}</span>
                              {showLogicalName && (
                                <span className="font-normal text-muted-foreground">
                                  ({table.logicalName})
                                </span>
                              )}
                            </span>
                            {apiEndpoint && (
                              <BcApiEndpointBadge endpoint={apiEndpoint} />
                            )}
                            {sfTable && (
                              <SalesforceEntityTypeBadge isCustom={sfTable.isCustom ?? false} />
                            )}
                          </Label>
                          {table.disabledReason ? (
                            <p className="text-xs text-muted-foreground">
                              {(t.wizard.availableTableDisabledReasons as Record<string, string>)[table.disabledReason] ?? table.disabledReason}
                            </p>
                          ) : tooLong && (
                            <p className="text-xs text-muted-foreground">
                              {strings.nameTooLongForPrefix}
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && apiEndpoint === 'V2' && config?.timestampField && (
                        <Alert className="items-center [&>svg]:translate-y-0">
                          <Info className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            {strings.incrementalSyncVia.replace('{field}', config.timestampField)}
                          </AlertDescription>
                        </Alert>
                      )}
                      {isSelected && apiEndpoint === 'V2' && !config?.timestampField && (
                        <Alert className="items-center [&>svg]:translate-y-0">
                          <Info className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            {strings.noTimestampFieldNotice}
                          </AlertDescription>
                        </Alert>
                      )}
                      {isSelected && apiEndpoint === 'ODataV4' && dateTimeFields && dateTimeFields.length > 0 && (
                        <div className="flex flex-col gap-1 rounded-md border px-4 py-3">
                          <Label className="text-xs text-muted-foreground mb-2">
                            {strings.timestampFieldLabel}
                          </Label>
                          <Select
                            value={config?.timestampField ?? '__none__'}
                            onValueChange={(selected) => { handleTimestampFieldChange(table.logicalName, selected); }}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder={strings.timestampFieldPlaceholder} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__" className="text-xs">
                                {strings.timestampFieldNone}
                              </SelectItem>
                              {dateTimeFields.map((field: string) => (
                                <SelectItem key={field} value={field} className="text-xs">
                                  {field}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {isSelected && apiEndpoint === 'ODataV4' && (!dateTimeFields || dateTimeFields.length === 0) && (
                        <Alert className="items-center [&>svg]:translate-y-0">
                          <Info className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            {strings.noTimestampFieldNotice}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                    </div> {/* px-4 pb-2 spacing wrapper */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
